import React, { useContext, useEffect, useState } from "react";
import { planTileKey, filterOwnerOnly, ownerFilterAvailable, backlogFilterPills, filterBacklogRows, IMPORT_FILE_ACCEPT } from "../api/shape.js";
import { rosterForProject } from "../api/speed.js";
import RecordReview from "../screens/day/RecordReview.jsx";
import { PhoneChip, PhoneFullScreen, PhoneSheet, PhoneShellContext, Svg, ICON } from "./PhoneShell.jsx";
import {
  bookDayLabel,
  bookingDayIndex,
  dayBookings,
  dayCountText,
  dayNumber,
  dayTitle,
  defaultDayIndex,
  isoWeekOf,
  moveChoices,
  nowText,
  weekRangeText,
} from "./planHelpers.js";

const CHIP_TONE = { bad: "bad", warn: "warn", ok: "ok", acc: "acc", mute: "mute" };

function scopeText(shell) {
  if (!shell) return "";
  const { selectedCodes, projectCodes } = shell;
  return selectedCodes.length === 0 || selectedCodes.length === projectCodes.length ? "All projects" : selectedCodes.join(", ");
}

/** "Waiting for a repair check · N": opens the list, oldest first (C6). */
function RepairChecksPhone({ repairChecks, initialOpen = false }) {
  const [open, setOpen] = useState(initialOpen);
  return (
    <section className="ph-section" data-testid="plan-repair-checks" style={{ gap: 8 }}>
      <button type="button" className="ph-card ph-rc-head" aria-expanded={open} data-testid="plan-repair-toggle" onClick={() => setOpen((v) => !v)}>
        <span className="ph-cardmeta" style={{ justifyContent: "space-between", width: "100%" }}>
          <span className="ph-cardtitle" style={{ fontFamily: "inherit" }}>Waiting for a repair check</span>
          <span className="ph-mono ph-rc-count" data-testid="plan-repair-count">{repairChecks.count}</span>
        </span>
        <span className="ph-small">We recorded defects and the yard has not asked us to see them again. {repairChecks.oldestText}</span>
        <span className="ph-small" style={{ color: "var(--ph-acc-text)" }}>{open ? "Hide the list" : "Show the list, oldest first"}</span>
      </button>
      {open ? (
        <ol className="ph-rc-list" data-testid="plan-repair-list">
          {repairChecks.items.map((i) => (
            <li className="ph-card" key={i.key} data-testid="plan-repair-item" data-inspection={i.inspectionNumber}>
              <span className="ph-cardtitle">{i.code ? `${i.code} · ` : ""}{i.line}</span>
              {i.defectText ? <span className="ph-small">{i.defectText}</span> : null}
            </li>
          ))}
        </ol>
      ) : null}
    </section>
  );
}

/** A forecast card: a button that opens the Book screen when booking is available, a plain card otherwise. */
function ForecastCardTag({ onBook, item, children, ...rest }) {
  if (!onBook) return <div className="ph-card dashed" style={{ textAlign: "left", alignItems: "flex-start" }} {...rest}>{children}</div>;
  return (
    <button type="button" className="ph-card dashed" style={{ textAlign: "left", alignItems: "flex-start", width: "100%", cursor: "pointer", font: "inherit" }} onClick={() => onBook(item)} {...rest}>
      {children}
    </button>
  );
}

/** The yard's forward schedule for the selected day, labelled Expected or Likely (C4). */
function ForecastPhone({ forecast, dayIndex, day, onBook }) {
  const cell = forecast.cells[dayIndex];
  if (!cell || cell.count === 0) return null;
  return (
    <section className="ph-section" style={{ marginTop: 6 }} data-testid="plan-forecast">
      <h2 className="ph-label">Yard&apos;s forward schedule{day ? ` · ${day.label} ${day.dayMonth}` : ""}</h2>
      {cell.items.map((item) => (
        <ForecastCardTag onBook={onBook} item={item} key={item.key} data-testid="plan-forecast-item" data-confidence={item.confidence}>
          <span className="ph-cardmeta"><PhoneChip tone={item.confidence === "expected" ? "acc" : "mute"}>{cell.label.toUpperCase()}</PhoneChip><span>Not yet requested</span></span>
          <span className="ph-cardtitle">{item.label}</span>
          {item.kind ? <span className="ph-small">{item.kind}</span> : null}
          {onBook ? <span className="ph-small" style={{ color: "var(--ph-acc-hi)" }}>Book…</span> : null}
        </ForecastCardTag>
      ))}
    </section>
  );
}

/** One booking on the selected day: time, the title (opens the inspection), and a Move control. */
function BookingCard({ item, saving, selected, onOpen, onMove }) {
  const { tile } = item;
  return (
    <div className={`ph-booking${selected ? " sel" : ""}`} data-testid="ph-booking" data-schedule-ref={tile.scheduleRef}>
      <span className="ph-booking-time">{tile.plannedTime || "no time"}</span>
      <button type="button" className="ph-booking-body" onClick={() => onOpen(item)}>
        <span className="t">{tile.title}</span>
        <span className="s">{[tile.code, item.laneName].filter(Boolean).join(" · ")}</span>
      </button>
      {saving ? (
        <PhoneChip tone="mute" data-testid="plan-tile-saving">Saving…</PhoneChip>
      ) : tile.chipText ? (
        <PhoneChip tone={CHIP_TONE[tile.chipClass] || "mute"}>{tile.chipText}</PhoneChip>
      ) : null}
      <button type="button" className="ph-iconbtn sq" aria-label="Move to another day" data-testid="ph-move" onClick={() => onMove(item)}>
        <Svg size={18}>{ICON.swap}</Svg>
      </button>
    </div>
  );
}

/** PlanItem.dc.html: the existing details panel as a full screen. */
function ItemScreen({ ctx, item, onBack, onMove }) {
  const { tile } = item;
  const detail = ctx.tileDetail(tile, ctx.runsById);
  const extras = ctx.detailExtras;
  const tone = CHIP_TONE[tile.chipClass] || "mute";
  const recorded = tile.row && tile.row.recordedInspectionNumber;
  const canOpenRecord = detail.primaryKind === "record" && Boolean(detail.recordNumber) && Boolean(ctx.onOpenRecord);
  return (
    <PhoneFullScreen
      title="Inspection"
      titleId="ph-item-title"
      onBack={onBack}
      backLabel="Back to Plan"
      testId="ph-plan-item"
      footerTwo={canOpenRecord}
      footer={
        <>
          <button type="button" className="ph-btn mid" data-testid="ph-item-move" onClick={() => onMove(item)}>Move</button>
          {canOpenRecord ? (
            <button type="button" className="ph-btn pri" data-testid="ph-item-open-record" onClick={() => ctx.onOpenRecord({ code: tile.code, inspectionNumber: detail.recordNumber })}>
              {detail.primaryLabel}
            </button>
          ) : null}
        </>
      }
    >
      <div className="ph-stack" style={{ gap: 6 }}>
        <span className="ph-meta">{tile.code}{recorded ? ` · CI-${recorded}` : ""}{tile.row && tile.row.inspectionType ? ` · ${tile.row.inspectionType}` : ""}</span>
        <h1 style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.25 }}>{detail.title}</h1>
        <span style={{ alignSelf: "flex-start" }}><PhoneChip tone={tone} className={`ph-chip big ${tone}`}>{tile.chipText || "booked"}</PhoneChip></span>
      </div>
      <dl className="ph-dl">
        {detail.facts.map(([k, v]) => (
          <React.Fragment key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </React.Fragment>
        ))}
      </dl>
      {extras && !extras.loading && extras.record ? <RecordReview view={extras.record} onReview={ctx.onReviewRecord} phone /> : null}
      {extras ? (
        <section className="ph-section" data-testid="plan-detail-photos">
          <h2 className="ph-label">Photos{extras.photos && extras.photos.length ? ` · ${extras.photos.length}` : ""}</h2>
          {extras.loading ? (
            <span className="ph-note">Loading photos…</span>
          ) : extras.photosError ? (
            <span style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <span data-testid="plan-detail-photos-error" style={{ color: "var(--ph-bad)", fontSize: 13 }}>Could not load photos</span>
              <button type="button" className="ph-smallbtn" onClick={ctx.onRetryDetail}>Retry</button>
            </span>
          ) : extras.photos && extras.photos.length ? (
            <div className="ph-photos two">
              {extras.photos.map((p) => (
                <img key={p.key} className="ph-photo" src={p.url} alt={p.caption || "Inspection photo"} />
              ))}
            </div>
          ) : (
            <span className="ph-note">No photos recorded</span>
          )}
        </section>
      ) : null}
      {detail.history.length ? (
        <section className="ph-section">
          <h2 className="ph-label">History</h2>
          {detail.history.map((line, i) => (
            <span key={i} className="ph-note" style={{ color: "var(--ph-t4)" }}>{line}</span>
          ))}
        </section>
      ) : null}
      {extras && !extras.loading && extras.ncr ? (
        <button
          type="button"
          className="ph-card link"
          style={{ background: "var(--ph-warn-bg)", borderColor: "var(--ph-warn-br)", color: "var(--ph-warn-ink)" }}
          data-testid="plan-detail-open-ncr"
          onClick={() => ctx.onOpenNcr && ctx.onOpenNcr({ code: extras.ncr.code, ncrNumber: extras.ncr.ncrNumber })}
        >
          <span className="ph-stack grow">
            <span className="ph-mono ph-small" style={{ color: "var(--ph-warn)" }}>NCR D{extras.ncr.ncrNumber}</span>
            <span className="ph-cardtitle">{extras.ncr.title || "Open the NCR"}</span>
          </span>
          <span className="ph-primary-text" style={{ color: "var(--ph-warn)" }}>Open NCR</span>
        </button>
      ) : null}
      {extras && !extras.loading && extras.ncrError ? (
        <span style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <span data-testid="plan-detail-ncr-error" style={{ color: "var(--ph-bad)", fontSize: 13 }}>NCR status unknown</span>
          <button type="button" className="ph-smallbtn" onClick={ctx.onRetryDetail}>Retry</button>
        </span>
      ) : null}
    </PhoneFullScreen>
  );
}

/**
 * PlanMove.dc.html: desktop drag-to-move has no touch equivalent, so Move
 * opens a day picker that calls the SAME handler a drag-drop calls
 * (onDrop(tile, { assignedTo, plannedDate })). The booking keeps its
 * inspector and its time, exactly as a drag does.
 */
function MoveSheet({ ctx, item, onClose, onMoved }) {
  const { model } = ctx;
  const { tile } = item;
  const choices = moveChoices(model, tile);
  const nowIndex = bookingDayIndex(model, tile);
  const [pick, setPick] = useState(() => choices.findIndex((c) => !c.current));
  const target = choices[pick];
  async function submit() {
    if (!target || target.current) return;
    onMoved();
    await ctx.onDrop(tile, { assignedTo: item.laneKey, plannedDate: target.day.date });
  }
  return (
    <PhoneSheet labelledBy="ph-move-title" onClose={onClose} testId="ph-move-sheet">
      <div className="ph-stack" style={{ gap: 4 }}>
        <h2 id="ph-move-title" className="ph-sheet-title">Move to another day</h2>
        <span className="ph-sheet-note">{tile.title} · {nowText(model.days[nowIndex], tile)}</span>
      </div>
      <div role="radiogroup" aria-label="New day" className="ph-radios">
        {choices.map((c, i) => (
          <button key={c.day.date} type="button" role="radio" aria-checked={pick === i} className={`ph-radio${pick === i ? " on" : ""}`} onClick={() => setPick(i)}>
            <span>{c.label}</span>
            <span className="c">{c.booked} booked</span>
          </button>
        ))}
      </div>
      {tile.plannedTime ? <span className="ph-note" data-testid="ph-move-time">The booking keeps its time, {tile.plannedTime}.</span> : null}
      <button type="button" className="ph-btn pri" disabled={!target || target.current} data-testid="ph-move-confirm" onClick={submit}>
        {target && !target.current ? `Move to ${target.day.label} ${target.day.dayMonth}` : "Pick another day"}
      </button>
    </PhoneSheet>
  );
}

/**
 * PlanBook.dc.html: book a "To book" row on a day, with an inspector from the
 * project roster. Calls the existing booking handlers: onDropPinned for a
 * re-inspection (books a new row), onDrop for an imported, unbooked row.
 */
function BookScreen({ ctx, target, defaultDay, onClose }) {
  const { model } = ctx;
  const { row, pinned } = target;
  const roster = rosterForProject(model.lanes, row.code);
  const [dayIndex, setDayIndex] = useState(defaultDay);
  const [inspector, setInspector] = useState(() => {
    const given = String(row.assignedTo || "").toLowerCase();
    return roster.some((r) => r.upn === given) ? given : "";
  });
  const day = model.days[dayIndex];
  async function submit() {
    if (!day) return;
    onClose();
    const where = { assignedTo: inspector, plannedDate: day.date };
    if (target.forecast) await ctx.onBookForecast(target.forecast, where);
    else if (pinned) await ctx.onDropPinned(row, where);
    else await ctx.onDrop(row, where);
  }
  const sub = pinned || row.kind === "reinspection" ? [row.code, "re-inspection", row.subId].filter(Boolean).join(" · ") : [row.code, row.category].filter(Boolean).join(" · ");
  return (
    <PhoneFullScreen
      title="Book in Plan"
      titleId="ph-book-title"
      cancelStyle
      backLabel="Cancel"
      onBack={onClose}
      testId="ph-plan-book"
      footer={<button type="button" className="ph-btn pri" data-testid="ph-book-confirm" disabled={!day} onClick={submit}>{day ? `Book on ${bookDayLabel(day)} ${day.dayMonth.split(" ")[1] || ""}`.trim() : "Pick a day"}</button>}
    >
      <div className="ph-card">
        <span className="ph-cardmeta">{sub}</span>
        <span className="ph-cardtitle" style={{ fontSize: 16 }}>{row.title}</span>
        {row.sub ? <span className="ph-cardsub">{row.sub}</span> : null}
      </div>
      <span className="ph-field" style={{ marginBottom: -10 }}>Day</span>
      <div role="radiogroup" aria-label="Day" className="ph-radiogrid" style={{ gridTemplateColumns: `repeat(${Math.max(1, model.days.length)}, minmax(0, 1fr))` }}>
        {model.days.map((d, i) => (
          <button key={d.date} type="button" role="radio" aria-checked={dayIndex === i} className={`ph-radio${dayIndex === i ? " on" : ""}`} onClick={() => setDayIndex(i)}>
            {bookDayLabel(d)}
          </button>
        ))}
      </div>
      {row.category ? (
        <dl className="ph-dl"><dt>Trade</dt><dd>{row.category}</dd></dl>
      ) : null}
      <label className="ph-field">
        Inspector
        <select className="ph-select" value={inspector} onChange={(e) => setInspector(e.target.value)}>
          <option value="">Unassigned</option>
          {roster.map((r) => (
            <option key={r.upn} value={r.upn}>{r.name}</option>
          ))}
        </select>
      </label>
      <span className="ph-note" style={{ marginTop: -8 }}>The inspector sees the booking in the capture app.</span>
    </PhoneFullScreen>
  );
}

/** The existing "+ Schedule" form (the existing upsert) as a full phone screen. */
function ScheduleScreen({ ctx, onClose }) {
  const { ScheduleForm } = ctx;
  return (
    <PhoneFullScreen title="Schedule an inspection" titleId="ph-schedule-title" cancelStyle backLabel="Cancel" onBack={onClose} testId="ph-plan-schedule">
      <ScheduleForm projects={ctx.scheduleProjects} lanes={ctx.model.lanes} today={ctx.scheduleToday} onCreate={ctx.onScheduleCreate} onClose={onClose} />
    </PhoneFullScreen>
  );
}

function ImportCandidate({ row, checked, onToggle, statusLabel }) {
  const writable = row.status === "new" || row.status === "changed" || row.status === "reinstate" || row.kind === "cancel";
  return (
    <label className="ph-candidate" data-testid="plan-import-candidate">
      <input type="checkbox" checked={checked} disabled={!writable} aria-label={row.label} onChange={writable ? onToggle : undefined} data-testid="plan-import-candidate-checkbox" />
      <span className="ph-stack grow">
        <span className="ph-cardtitle" style={{ fontSize: 14 }}>{row.label}</span>
        <span className="ph-mono ph-small">{[row.date || "—", row.type].filter(Boolean).join(" · ")}</span>
      </span>
      <PhoneChip tone={row.status === "new" || row.status === "reinstate" ? "acc" : row.status === "changed" ? "warn" : row.status === "retiring" ? "bad" : "mute"}>{statusLabel(row.status)}</PhoneChip>
    </label>
  );
}

function HistoryRows({ ctx, history }) {
  if (!history) return null;
  return (
    <section className="ph-section" data-testid="plan-import-history">
      <h2 className="ph-label">Import history</h2>
      {history.error ? <span data-testid="plan-import-history-error" style={{ color: "var(--ph-bad)", fontSize: 13 }}>Could not load import history ({history.error}).</span> : null}
      {!history.error && !history.loading && history.runs.length === 0 ? <span className="ph-note">No yard schedule has been imported for this project.</span> : null}
      {history.runs.map((run) => (
        <div key={run.runId} className="ph-historyrow" data-testid="plan-import-history-row">
          <span style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <span className="ph-cardtitle" style={{ fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{run.fileName || run.runId}</span>
            <span className="ph-mono ph-small" style={{ flexShrink: 0 }}>{run.importedAt ? run.importedAt.slice(0, 16).replace("T", " ") : "—"}</span>
          </span>
          <span className="ph-small">{ctx.formatImportCounts(run.counts)}{run.importedBy ? ` · ${run.importedBy}` : ""}</span>
          {ctx.showRunOnPlan ? (
            <button type="button" className="ph-smallbtn" style={{ alignSelf: "flex-start", marginTop: 4 }} data-testid="plan-import-history-show" onClick={() => ctx.showRunOnPlan(run)}>Show on plan</button>
          ) : null}
        </div>
      ))}
      {history.loading ? <span className="ph-note">Loading…</span> : null}
      {history.hasMore && !history.loading ? (
        <button type="button" className="ph-btn" data-testid="plan-import-history-more" onClick={ctx.onMoreHistory}>Show older imports</button>
      ) : null}
    </section>
  );
}

/** Import.dc.html (preview) and ImportDone.dc.html (applying, applied, and the history). */
function ImportScreen({ ctx }) {
  const { importPanel: panel } = ctx;
  const phase = panel.phase;
  const counts = panel.counts || [];
  const candidates = panel.candidates || [];
  const ownerOnly = Boolean(panel.ownerOnly);
  const filterAvailable = ownerFilterAvailable(candidates);
  const visible = filterOwnerOnly(candidates, ownerOnly && filterAvailable);
  const selectedKeys = panel.selectedKeys || new Set();

  // The history appears once the import has been applied.
  const { loadHistoryIfClosed } = ctx;
  useEffect(() => {
    if (phase === "committed") loadHistoryIfClosed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  if (phase === "committing" || phase === "committed") {
    const progress = panel.progress;
    // The run this import just made, from the history the screen loads: its first planned day is the week to go to.
    const changedRun = ctx.history && ctx.history.runs ? ctx.history.runs.find((r) => r.runId === panel.runId) || null : null;
    const pct = progress && Number(progress.total) > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
    return (
      <PhoneFullScreen
        title={`Yard schedule${ctx.importCode ? ` · ${ctx.importCode}` : ""}`}
        titleId="ph-import-title"
        onBack={phase === "committed" ? ctx.onImportDismiss : () => {}}
        backLabel="Back to Plan"
        testId="ph-plan-import-done"
      >
        {phase === "committing" ? (
          <div role="status" className="ph-card statusbox">
            <span style={{ fontSize: 16, fontWeight: 600 }}>{ctx.applyingLabel(progress)}</span>
            <div className="ph-bar"><span style={{ width: `${pct || 40}%` }} /></div>
            <span style={{ fontSize: 13, color: "var(--ph-acc-text)" }}>Keep this screen open until it finishes.</span>
          </div>
        ) : (
          <div role="status" className="ph-card okbox">
            <span style={{ color: "var(--ph-ok)" }}><Svg size={24} stroke={2.2}>{ICON.checkCircle}</Svg></span>
            <span className="ph-stack">
              <span style={{ fontSize: 16, fontWeight: 600, color: "var(--ph-ok-ink)" }}>Applied. The schedule is live for this project.</span>
              <span data-testid="plan-import-counts" style={{ fontSize: 13, color: "var(--ph-ok-ink2)" }}>{counts.map((c) => `${c.count} ${c.label}`).join(" · ")}</span>
            </span>
          </div>
        )}
        {phase === "committed" && changedRun && changedRun.firstPlannedDate ? (
          <button
            type="button"
            className="ph-btn mid sec"
            data-testid="ph-import-first-week"
            onClick={() => {
              ctx.showRunOnPlan(changedRun);
              ctx.onImportDismiss();
            }}
          >
            Go to the first changed week · W{isoWeekOf(changedRun.firstPlannedDate)}
          </button>
        ) : null}
        {phase === "committed" ? <button type="button" className="ph-btn mid" onClick={ctx.onImportDismiss}>Close</button> : null}
        <HistoryRows ctx={ctx} history={ctx.history} />
      </PhoneFullScreen>
    );
  }

  return (
    <PhoneFullScreen
      title="Import yard schedule"
      titleId="ph-import-title"
      cancelStyle
      backLabel="Cancel"
      onBack={ctx.onImportDismiss}
      testId="ph-plan-import"
      footer={
        phase === "preview" ? (
          <button type="button" className="ph-btn pri" data-testid="ph-import-apply" onClick={ctx.onImportCommit}>
            {candidates.length ? `Apply ${selectedKeys.size} change${selectedKeys.size === 1 ? "" : "s"}` : "Apply"}
          </button>
        ) : null
      }
    >
      {ctx.importCode ? (
        <dl className="ph-dl" style={{ gridTemplateColumns: "80px 1fr" }}><dt>Project</dt><dd className="ph-mono ph-strong">{ctx.importCode}</dd></dl>
      ) : null}
      {phase === "preview" ? (
        <div className="ph-card link" data-testid="ph-import-file" style={{ cursor: "default" }}>
          <span className="ph-stack grow">
            <span className="ph-cardtitle" style={{ fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{panel.fileName || "Yard schedule file"}</span>
            <span className="ph-small">{counts.length ? `${counts.reduce((n, c) => n + (Number(c.count) || 0), 0)} rows read` : "File read"}</span>
          </span>
          <button type="button" className="ph-smallbtn" style={{ height: 44 }} data-testid="ph-import-change" onClick={ctx.onImportButtonClick}>Change</button>
        </div>
      ) : null}
      {phase === "uploading" ? (
        <div role="status" className="ph-card statusbox">
          <span style={{ fontSize: 16, fontWeight: 600 }}>Uploading and reading…</span>
          <div className="ph-bar run"><span /></div>
        </div>
      ) : null}
      {phase === "error" ? (
        <div role="alert" className="ph-card badbox" data-testid="plan-import-error">
          <span style={{ color: "var(--ph-bad-ink)" }}>{panel.message}</span>
        </div>
      ) : null}
      {phase === "preview" ? (
        <>
          <section className="ph-section">
            <h2 className="ph-label">What changes</h2>
            <div className="ph-counts" data-testid="plan-import-counts">
              {counts.map((c) => (
                <div key={c.label} className="ph-count"><span className="v">{c.count}</span><span className="l">{c.label}</span></div>
              ))}
            </div>
            <span className="ph-note">Nothing applied yet.</span>
          </section>
          {candidates.length > 0 ? (
            <section className="ph-section">
              <h2 className="ph-label">Rows · <span data-testid="plan-import-selected-count">{selectedKeys.size} selected</span></h2>
              <label className="ph-checkrow" style={{ minHeight: 44, border: "1px solid var(--ph-line)", borderRadius: 10, opacity: filterAvailable ? 1 : 0.6 }}>
                <input type="checkbox" data-testid="plan-import-owner-only" checked={ownerOnly} disabled={!filterAvailable} onChange={ctx.onImportToggleOwnerOnly} />
                <span style={{ fontSize: 14 }}>Owner inspections only</span>
              </label>
              {!filterAvailable ? <span className="ph-small">This file&apos;s format does not identify which inspections the owner attends — showing every row.</span> : null}
              <div className="ph-two">
                <button type="button" className="ph-btn" onClick={ctx.onImportSelectAllShown}>Select all shown</button>
                <button type="button" className="ph-btn" onClick={ctx.onImportClearSelection}>Clear</button>
              </div>
              {panel.candidatesTruncated ? (
                <span data-testid="plan-import-truncated" className="ph-small" style={{ color: "var(--ph-warn)" }}>
                  Showing {candidates.length} of {panel.candidatesTotal} rows — the rest are not listed and will not be imported unless you narrow the file or run the import again.
                </span>
              ) : null}
              <div className="ph-stack" data-testid="plan-import-candidates" style={{ gap: 6 }}>
                {visible.map((row) => (
                  <ImportCandidate key={row.key} row={row} checked={selectedKeys.has(row.key)} statusLabel={ctx.importStatusLabel} onToggle={() => ctx.onImportToggleRow(row.key)} />
                ))}
              </div>
            </section>
          ) : null}
        </>
      ) : null}
    </PhoneFullScreen>
  );
}

function HistoryScreen({ ctx, onBack }) {
  return (
    <PhoneFullScreen title={`Import history${ctx.importHistoryScope ? ` · ${ctx.importHistoryScope}` : ""}`} titleId="ph-history-title" onBack={onBack} backLabel="Back to Plan" testId="ph-plan-history">
      <HistoryRows ctx={ctx} history={ctx.history} />
    </PhoneFullScreen>
  );
}

/**
 * Plan on a phone (Plan.dc.html and its screens). Everything it does is the
 * desktop Plan's own state and handlers (`ctx`, built in Plan.jsx): the week
 * arrows, the details panel, the drag-drop move/booking handlers, the
 * schedule form, the yard schedule import and its history.
 */
export default function PhonePlan({ ctx }) {
  const { model, weekNo, loadingWeekNo, stale, weekLoading } = ctx;
  const shell = useContext(PhoneShellContext);
  const [dayIndex, setDayIndex] = useState(() => defaultDayIndex(model));
  const [itemOpen, setItemOpen] = useState(null); // { tile, laneKey, laneName }
  const [moveFor, setMoveFor] = useState(null);
  const [bookFor, setBookFor] = useState(() => (ctx.pinnedBooking ? { row: ctx.pinnedBooking, pinned: true } : null));
  const [scheduleOpen, setScheduleOpen] = useState(false);
  // The history list is loaded for the "applied" screen too; only the button below opens it as its own screen.
  const [historyShown, setHistoryShown] = useState(false);
  const { backlogFilter, setBacklogFilter } = ctx;

  // A different week lands on its own default day.
  useEffect(() => {
    setDayIndex(defaultDayIndex(model));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model.weekStart]);
  // Today's "Book in Plan" hands a re-inspection over: open its Book screen.
  useEffect(() => {
    if (ctx.pinnedBooking) setBookFor({ row: ctx.pinnedBooking, pinned: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.pinnedBooking]);
  // "Open in Plan" (Report / Today) selects a booking: show it.
  useEffect(() => {
    const sel = ctx.selected;
    if (!ctx.focusNonce || !sel || sel.kind !== "tile") return;
    const i = bookingDayIndex(model, sel.data);
    if (i < 0) return;
    setDayIndex(i);
    const found = dayBookings(model, i).find((b) => planTileKey(b.tile) === planTileKey(sel.data));
    if (found) setItemOpen(found);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.focusNonce]);

  const day = model.days[dayIndex] || model.days[0];
  const bookings = day ? dayBookings(model, dayIndex) : [];
  const backlogPills = backlogFilterPills(model.backlog);
  const backlogVisible = filterBacklogRows(model.backlog, backlogFilter);
  const importOpen = Boolean(ctx.importPanel);
  const historyOpen = !importOpen && historyShown && Boolean(ctx.history);

  function openItem(item) {
    ctx.selectTile(item.tile);
    setItemOpen(item);
  }
  // The item screen always shows the booking as the model has it now (a move changes its day).
  const liveItem = itemOpen
    ? dayBookings(model, Math.max(0, bookingDayIndex(model, itemOpen.tile) >= 0 ? bookingDayIndex(model, itemOpen.tile) : dayIndex)).find(
        (b) => planTileKey(b.tile) === planTileKey(itemOpen.tile)
      ) || itemOpen
    : null;

  return (
    <div className="ph-screen" data-testid="phone-plan">
      <header className="ph-head tight">
        <div className="ph-titlerow">
          <h1 className="ph-h1">Plan</h1>
          <div style={{ display: "flex", gap: 6 }}>
            <button type="button" className="ph-iconbtn" aria-label="Previous week" disabled={Boolean(weekLoading)} onClick={ctx.onPrevWeek}><Svg size={16}>{ICON.prev}</Svg></button>
            <button type="button" className="ph-iconbtn" aria-label="Next week" disabled={Boolean(weekLoading)} onClick={ctx.onNextWeek}><Svg size={16}>{ICON.next}</Svg></button>
          </div>
        </div>
        <span className="ph-meta" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {shell && shell.openProjects ? (
            <button type="button" className="ph-scope" data-testid="ph-project-pill" aria-haspopup="dialog" aria-label={`Projects shown: ${scopeText(shell)}. Change`} onClick={shell.openProjects}>
              {[scopeText(shell), weekRangeText(model, weekNo)].filter(Boolean).join(" · ")}
            </button>
          ) : (
            <span>{weekRangeText(model, weekNo)}</span>
          )}
          {loadingWeekNo ? <PhoneChip tone="mute" data-testid="plan-week-loading">Loading week {loadingWeekNo}…</PhoneChip> : null}
          {stale && !loadingWeekNo ? (
            <button type="button" className="ph-chip warn" data-testid="plan-week-stale" onClick={ctx.onRetryStale} style={{ background: "transparent", cursor: "pointer" }}>Not up to date — Retry</button>
          ) : null}
        </span>
        <div role="tablist" aria-label="Day" className="ph-days">
          {model.days.map((d, i) => (
            <button key={d.date} type="button" role="tab" aria-selected={i === dayIndex} className={`ph-day${i === dayIndex ? " on" : ""}`} data-testid="ph-day" onClick={() => setDayIndex(i)}>
              <span className="n1">{d.label}</span>
              <span className="n2">{dayNumber(d)}</span>
              <span className="n3">{dayCountText(dayBookings(model, i).length)}</span>
            </button>
          ))}
        </div>
      </header>

      <main className="ph-main tight">
        {model.errors.length > 0 ? (
          <div className="ph-stack" data-testid="plan-errors" style={{ gap: 8 }}>
            {model.errors.map((e) => (
              <div className="ph-card" key={e.code}><span className="ph-cardmeta"><PhoneChip tone="bad">!</PhoneChip>{e.code}</span><span style={{ color: "var(--ph-bad)" }}>Unavailable &mdash; {e.error}</span></div>
            ))}
          </div>
        ) : null}
        {ctx.notice ? <div role="status" className="ph-card warnbox" data-testid="plan-notice"><span>{ctx.notice}</span></div> : null}
        {model.repairChecks.count > 0 ? <RepairChecksPhone repairChecks={model.repairChecks} initialOpen={ctx.initialRepairOpen} /> : null}
        <h2 className="ph-label" style={{ margin: "0 0 2px" }}>{day ? dayTitle(day, bookings.length) : ""}</h2>
        {bookings.length ? (
          bookings.map((item) => (
            <BookingCard
              key={planTileKey(item.tile)}
              item={item}
              saving={Boolean(ctx.savingRef) && item.tile.scheduleRef === ctx.savingRef}
              selected={false}
              onOpen={openItem}
              onMove={(it) => { ctx.selectTile(it.tile); setMoveFor(it); }}
            />
          ))
        ) : (
          <div className="ph-card dashed">Nothing scheduled this day</div>
        )}

        {model.forecast.hasAny ? <ForecastPhone forecast={model.forecast} dayIndex={dayIndex} day={day} onBook={ctx.onBookForecast ? (item) => setBookFor({ forecast: item, row: { code: item.code, title: item.label, category: "", sub: "Not yet requested by the yard", assignedTo: "" }, pinned: false }) : undefined} /> : null}

        <section className="ph-section" style={{ marginTop: 12 }} data-testid="ph-tobook">
          <h2 className="ph-label" data-testid="plan-backlog-count">To book · {model.backlog.length} of {model.backlogTotal}</h2>
          {backlogPills.length > 1 ? (
            <div data-testid="plan-backlog-filters" className="ph-chips" style={{ margin: "0 -20px" }}>
              {backlogPills.map((p) => (
                <button key={p.id} type="button" className={`ph-chipbtn${backlogFilter === p.id ? " on" : ""}`} onClick={() => setBacklogFilter(p.id)}>{p.label}</button>
              ))}
            </div>
          ) : null}
          {ctx.pinnedBooking ? (
            <button type="button" className="ph-linkrow acc" onClick={() => setBookFor({ row: ctx.pinnedBooking, pinned: true })}>
              <span>{ctx.pinnedBooking.title} · book</span>
              <Svg size={18}>{ICON.plus}</Svg>
            </button>
          ) : null}
          {backlogVisible.length === 0 && !ctx.pinnedBooking ? <div className="ph-card"><span className="ph-note">Nothing to do</span></div> : null}
          {backlogVisible.map((row) => (
            <div className="ph-card" key={row.key} data-testid={row.kind === "reinspection" ? "plan-backlog-reinspection-row" : "plan-unbooked-row"} data-schedule-ref={row.kind === "reinspection" ? "" : row.scheduleRef || ""}>
              <span className="ph-cardmeta">
                <PhoneChip tone={CHIP_TONE[row.chipClass] || "mute"}>{row.chipText}</PhoneChip>
                <span>{[row.code, row.kind === "reinspection" ? row.sub : null, row.category && row.category !== row.sub ? row.category : null].filter(Boolean).join(" · ")}</span>
              </span>
              <span className="ph-cardtitle">{row.title}</span>
              <button type="button" className="ph-btn sec" disabled={Boolean(ctx.savingRef) && (row.scheduleRef || row.sourceInspectionNumber) === ctx.savingRef} onClick={() => setBookFor({ row, pinned: row.kind === "reinspection" })}>
                Book on {day ? `${day.label} ${dayNumber(day)}` : "a day"}
              </button>
            </div>
          ))}
          {ctx.canShowMoreBacklog ? (
            <button type="button" className="ph-btn" data-testid="plan-backlog-show-more" disabled={ctx.backlogLoadingMore} onClick={ctx.onShowMoreBacklog}>
              {ctx.backlogLoadingMore ? "Loading…" : ctx.showMoreCount > 0 ? `Show ${ctx.showMoreCount} more` : "Show more"}
            </button>
          ) : null}
        </section>

        {model.forecast.laterWeeks.length > 0 ? (
          <section className="ph-section" data-testid="plan-later-weeks">
            <h2 className="ph-label">Later weeks · from the yard&apos;s block schedule</h2>
            {model.forecast.laterWeeks.map((w) => (
              <div className="ph-card dashed" style={{ textAlign: "left", alignItems: "flex-start" }} key={w.weekStart} data-testid="plan-later-week">
                <span className="ph-mono ph-small">{w.label}</span>
                <span className="ph-cardtitle">{w.text}</span>
              </div>
            ))}
            <span className="ph-small">The further ahead, the less exact. These are the yard&apos;s plan and move often.</span>
          </section>
        ) : null}

        <section className="ph-section" data-testid="plan-yard-schedule-panel">
          <h2 className="ph-label">Yard schedule</h2>
          <span className="ph-small ph-mono" data-testid="plan-last-import">{model.lastImport ? model.lastImport.message : "No yard schedule has been imported for this project."}</span>
          <input ref={ctx.fileInputRef} type="file" accept={IMPORT_FILE_ACCEPT} data-testid="plan-import-file-input" style={{ display: "none" }} onChange={ctx.onFileInputChange} />
          <button type="button" className="ph-linkrow plain" data-testid="plan-import-button" onClick={ctx.onImportButtonClick}>
            Import yard schedule
            <Svg size={18}>{ICON.download}</Svg>
          </button>
          <button type="button" className="ph-linkrow plain" data-testid="plan-import-history-button" disabled={!ctx.historyEnabled} onClick={() => { setHistoryShown(true); if (!ctx.history) ctx.onToggleHistory(); }} style={ctx.historyEnabled ? undefined : { opacity: 0.5 }}>
            Import history
          </button>
          <button type="button" className="ph-linkrow plain" data-testid="plan-schedule-button" disabled={!ctx.onScheduleCreate} onClick={() => setScheduleOpen(true)}>
            + Schedule
          </button>
        </section>
      </main>

      {liveItem ? <ItemScreen ctx={{ ...ctx, tileDetail: ctx.tileDetail }} item={liveItem} onBack={() => setItemOpen(null)} onMove={(it) => setMoveFor(it)} /> : null}
      {moveFor ? <MoveSheet ctx={ctx} item={moveFor} onClose={() => setMoveFor(null)} onMoved={() => { setMoveFor(null); setItemOpen(null); }} /> : null}
      {bookFor ? <BookScreen ctx={ctx} target={bookFor} defaultDay={dayIndex} onClose={() => setBookFor(null)} /> : null}
      {scheduleOpen && ctx.onScheduleCreate ? <ScheduleScreen ctx={ctx} onClose={() => setScheduleOpen(false)} /> : null}
      {importOpen ? <ImportScreen ctx={ctx} /> : null}
      {historyOpen ? <HistoryScreen ctx={ctx} onBack={() => setHistoryShown(false)} /> : null}
    </div>
  );
}
